import { Component, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BaseGridComponent } from "@shared/components/grid/components/base-grid/base-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { PageSizeService } from "@shared/services/page-size.service";
import { GridApi, RowModelType } from "ag-grid-community";
import { ColDef } from "ag-grid-enterprise";
import { ColDefCommunityExtended, FilterOption, GridFilterModel } from "@models/grid/grid-filter";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { SearchInputComponent } from "@shared/controls/search-input/search-input.component";
import {
  GridColumnSearchFilterComponent
} from "../grid-column-search-filter/grid-column-search-filter.component";
import {
  GridColumnsFilterComponent
} from "../grid-columns-filter/grid-columns-filter.component";

interface DetailRendererParams {
  detailGridOptions: { columnDefs: ColDefCommunityExtended[] };
}

@Component({
  selector: 'app-grid-client-side',
  imports: [
    FormsModule,
    GridColumnsFilterComponent,
    LoaderComponent,
    ReactiveFormsModule,
    SearchInputComponent,
    ListGridClientSideComponent,
    GridColumnSearchFilterComponent,
  ],
  standalone: true,
  providers: [{ provide: PageSizeService, useClass: PageSizeService }],
  templateUrl: 'grid-client-side.component.html',
  styleUrl: 'grid-client-side.component.scss'
})
export class GridClientSideComponent extends BaseGridComponent implements OnInit {
  @Input({ required: true }) override colDefs: ColDefCommunityExtended[] = [];

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  override readonly rowModelType: RowModelType = 'clientSide';

  gridApi!: GridApi;
  detailColDefs: ColDefCommunityExtended[] = [];
  rowDataCopy: unknown[] = [];

  ngOnInit(): void {
    if (this.pinFirstColumnName) {
      this.gridOptions = {
        ...(this.gridOptions || {}),
        onColumnMoved: () => this.ensureColumnAtFirst()
      };
    }

    if ((this.detailCellRendererParams as DetailRendererParams)?.detailGridOptions?.columnDefs) {
      this.detailColDefs = (this.detailCellRendererParams as DetailRendererParams).detailGridOptions.columnDefs;
      if (!this.rowDataCopy.length) {
        this.rowDataCopy = JSON.parse(JSON.stringify(this.rowData)) as unknown[];
      }
    }
  }

  ensureColumnAtFirst() {
    const currentState = this.gridApi.getColumnState();
    const index = currentState.findIndex(col => col.colId === this.pinFirstColumnName);
    if (index === -1 || index === 0) {
      return;
    }
    const newState = [...currentState];
    const [colState] = newState.splice(index, 1);

    newState.unshift(colState);

    this.gridApi.applyColumnState({
      state: newState,
      applyOrder: true
    });
  }

  getSavedColumnState(): any[] | null {
    const columnState = this.storageKey ? localStorage.getItem(this.storageKey) : null;
    return columnState ? JSON.parse(columnState) : null;
  }

  onCheckboxChange(colDef: ColDef): void {
    this.colDefs = this.colDefs.map((c) => ({ ...c, hide: c.field === colDef.field ? !c.hide : c.hide }));
    this.gridApi?.setColumnsVisible([colDef.field as unknown as string], !colDef.hide);
  }

  onGroupCheckboxChange(colDef: ColDefCommunityExtended): void {
    this.colDefs = this.colDefs.map((c) => {
      return c?.groupId === colDef.groupId ? colDef : c;
    });

    colDef?.children?.forEach(c => {
      if (c.field) {
        this.gridApi?.setColumnsVisible([c.field], !colDef.hide);
      }
    });
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    this.initFilter();
    this.gridReady.emit(this.gridApi);
  }

  onSelect(value: unknown): void {
    this.select.emit(value);
  }

  onSelectAll(value: boolean): void {
    this.selectAll.emit(value);
  }

  applyGroupFilter(): void {
    this.rowData = JSON.parse(JSON.stringify(this.rowDataCopy)) as unknown[];

    const filterModel: GridFilterModel = this.gridService.getGridFilterModelState();
    this.rowData = (this.rowData as { callRecords: Record<string, unknown>[] }[]).map(data => {
      Object.keys(filterModel).forEach(key => {
        const keys = key.split('.');
        const filteredCallRecords = data.callRecords.filter(record => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const callRecordValue = keys.reduce((obj, prop) => obj[prop], record);
          return filterModel[key].values?.includes(callRecordValue);
        });
        data = { ...data, callRecords: filteredCallRecords };
      });

      return data;
    });

    this.rowData = (this.rowData as { callRecords: Record<string, unknown>[] }[]).filter(row => row.callRecords.length);
  }

  initFilter(): void {
    const initialFilterModel: GridFilterModel = {};
    const savedState = this.getSavedColumnState();

    this.colDefs.forEach((c: ColDefCommunityExtended) => {
      const field = c.field ?? '';
      const options: FilterOption[] = c.filterParams?.filterOptions;
      const activeOptions = options?.filter(option => option?.active);

      if (savedState) {
        const columnState = savedState.find((state: { colId: string }) => state.colId === field);
        if (columnState) {
          c.hide = columnState.hide;
        }
      }

      if (activeOptions?.length) {
        c.filterTagShowed = true;
        initialFilterModel[field] = { filterType: 'set', values: activeOptions.map(option => option.value) };
      } else {
        c.filterTagShowed = false;
      }
    });

    this.gridApi?.setFilterModel(initialFilterModel);
  }

  get quickFilterTextValue(): string {
    return this.form.get('search')!.value!;
  }
}
